import produce from 'immer';
import { ActionsUnion, createAction } from 'utils/actionHelpers';
import { IPriceClass } from 'types/product';
import { getPrice, getBonus } from 'utils/helpers';

export enum ActionTypes {
  FETCH_CART_SUCCESS = 'FETCH_CART_SUCCESS',

  CART_ADD_OR_UPDATE = 'CART_ADD_OR_UPDATE',

  CART_PRODUCT_ADD = 'CART_PRODUCT_ADD',
  CART_PRODUCT_ADD_SUCCESS = 'CART_PRODUCT_ADD_SUCCESS',
  CART_PRODUCT_ADD_FAILURE = 'CART_PRODUCT_ADD_FAILURE',

  CART_PRODUCT_UPDATE = 'CART_PRODUCT_UPDATE',
  CART_PRODUCT_UPDATE_SUCCESS = 'CART_PRODUCT_UPDATE_SUCCESS',
  CART_PRODUCT_UPDATE_FAILURE = 'CART_PRODUCT_UPDATE_FAILURE',

  CART_PRODUCT_DELETE = 'CART_PRODUCT_DELETE',
  CART_PRODUCT_DELETE_SUCCESS = 'CART_PRODUCT_DELETE_SUCCESS',
  CART_PRODUCT_DELETE_FAILURE = 'CART_PRODUCT_DELETE_FAILURE',

  CART_PRODUCT_DISCOUNT = 'CART_PRODUCT_DISCOUNT',
}

export interface ICartProduct {
  no: number;
  product_id: number;
  name: string;
  description: string;
  category_id: number;
  car_classes_id: number;
  quantity: number;
  product_price_classes: IPriceClass[];
  image: string;
  discount?: any;
  agent_fee_pct: number;
  top_discount_pct: number;
  top_agent_fee_pct: number;
  agent_fee_rub: number;
}

export interface IAddCartProduct {
  product_id: number;
  car_classes_id: number;
  quantity: number;
}

export const initialState = {
  products: [],
};

export const Actions = {
  fetchCartSuccess: (payload: ICartProduct[]) =>
    createAction(ActionTypes.FETCH_CART_SUCCESS, payload),

  addOrUpdateToCart: (payload: IAddCartProduct) =>
    createAction(ActionTypes.CART_ADD_OR_UPDATE, payload),

  addToCart: (payload: IAddCartProduct) =>
    createAction(ActionTypes.CART_PRODUCT_ADD, payload),
  addToCartSuccess: (payload: ICartProduct) =>
    createAction(ActionTypes.CART_PRODUCT_ADD_SUCCESS, payload),

  updateProduct: (payload: { no: number, car_classes_id: number, quantity: number}) =>
    createAction(ActionTypes.CART_PRODUCT_UPDATE, payload),
  updateProductSuccess: (payload: { no: number, car_classes_id: number, quantity: number}) =>
    createAction(ActionTypes.CART_PRODUCT_UPDATE_SUCCESS, payload),

  deleteFromCart: (payload: number) =>
    createAction(ActionTypes.CART_PRODUCT_DELETE, payload),
  deleteFromCartSuccess: (payload: number) =>
    createAction(ActionTypes.CART_PRODUCT_DELETE_SUCCESS, payload),

  setDiscount: (payload: {no: number, discount: number}) =>
    createAction(ActionTypes.CART_PRODUCT_DISCOUNT, payload),
};

export interface ICartState {
  products: ICartProduct[];
}

export type CartAction = ActionsUnion<typeof Actions>;

export default (state: ICartState = initialState, action: CartAction) =>
  produce(state, (draft: ICartState) => {
    switch (action.type) {
      case ActionTypes.FETCH_CART_SUCCESS:
        draft.products = action.payload;
        return;
      case ActionTypes.CART_PRODUCT_ADD_SUCCESS:
        draft.products.push(action.payload);
        return;
      case ActionTypes.CART_PRODUCT_UPDATE_SUCCESS: {
        const index = draft.products.findIndex(i => i.no === action.payload.no);
        draft.products[index].car_classes_id = action.payload.car_classes_id;
        draft.products[index].quantity = action.payload.quantity;
        return;
      }

      case ActionTypes.CART_PRODUCT_DELETE_SUCCESS:
        draft.products = draft.products.filter(i => i.no !== action.payload);
        return;

      case ActionTypes.CART_PRODUCT_DISCOUNT:
        const index = draft.products.findIndex(i => i.no === action.payload.no);
        draft.products[index].discount = action.payload.discount;
    }
  });

  /**
   * Суммы по всему заказу
   * @param  products продукты
   * @param  ref_increase_pct доп. % рефералу
   */
  export const getPrices = (products: ICartProduct[], ref_increase_pct = 0) => {
    return(
      products.reduce(
        (allSumm, current) => {
          const {
            car_classes_id,
            product_price_classes,
            discount,
            top_discount_pct,
            quantity,
            agent_fee_pct,
            top_agent_fee_pct,
            agent_fee_rub,
          } = current;
          const selectedClass = car_classes_id;
          const selectedPrice = product_price_classes.find(
            i => i.car_class_id === selectedClass
          );
          let currentDiscount = parseFloat(discount);
          if (!currentDiscount) {
            currentDiscount = 0;
          }
          const resDiscount = allSumm.discount + currentDiscount!;
          const price = getPrice(selectedPrice!.price, top_discount_pct) * quantity;
          const resPrice = allSumm.price + price;
          const bonus =
            allSumm.bonus + getBonus(price, agent_fee_pct,  agent_fee_rub, top_agent_fee_pct, ref_increase_pct);
          return { price: resPrice, bonus, discount: resDiscount };
        },
        { price: 0, bonus: 0, discount: 0 }
      )
    )
  };

  export const getCartSize = (products: ICartProduct[]) => products.length;

  /**
   * Расчет цен, бонусов по каждому продукту
   */
  export const getCartPrices = (products: ICartProduct[], ref_increase_pct = 0) => {
    return products.map(({ no, discount = 0, ...i }) => {
      const selectedClass = i.product_price_classes.find(el => el.car_class_id === i.car_classes_id);
      const market_price = selectedClass!.market_price;
      const price = getPrice(selectedClass!.price, i.top_discount_pct);
      const bonus = getBonus(
        price,
        i.agent_fee_pct,
        i.agent_fee_rub,
        i.top_agent_fee_pct,
        ref_increase_pct,
        i.quantity,
        discount,
      );
      const bonusWithoutDiscount = getBonus(
        price,
        i.agent_fee_pct,
        i.agent_fee_rub,
        i.top_agent_fee_pct,
        ref_increase_pct,
        i.quantity,
      );
      const summ = Math.round((price * i.quantity - discount) * 100) / 100;
      const error = discount > bonusWithoutDiscount;
      return { no, market_price, price, bonus, summ, bonusWithoutDiscount, error }
    });
  };
